/**
 * Wire types for the `properties` module — derived field-by-field from
 * `serializeProperty` / `serializePropertyListRow` / `serializeUnit` /
 * `serializePropertyImage` (refactor/backend/src/modules/properties/serializers.ts).
 * snake_case verbatim, no camelCase mapping layer — see the FIELD-NAMING
 * DECISION note in src/types/user.ts.
 */

/** Django `properties.models.PropertyType`. */
export const PROPERTY_TYPES = [
  'APARTMENT',
  'HOUSE',
  'CONDO',
  'TOWNHOUSE',
  'COMMERCIAL',
  'OTHER',
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

/** Django `properties.models.PropertyStatus`. */
export const PROPERTY_STATUSES = ['PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'INACTIVE'] as const;
export type PropertyStatus = (typeof PROPERTY_STATUSES)[number];

/**
 * Kenya's 47 counties. Node-only — no Django reference. Backs the `state`
 * wire field's Kenya-market repurposing (see
 * refactor/backend/src/lib/kenyaCounties.ts): the field is NOT renamed at
 * the wire level, only what it's validated against and labeled as.
 */
export const KENYA_COUNTIES = [
  'Baringo',
  'Bomet',
  'Bungoma',
  'Busia',
  'Elgeyo-Marakwet',
  'Embu',
  'Garissa',
  'Homa Bay',
  'Isiolo',
  'Kajiado',
  'Kakamega',
  'Kericho',
  'Kiambu',
  'Kilifi',
  'Kirinyaga',
  'Kisii',
  'Kisumu',
  'Kitui',
  'Kwale',
  'Laikipia',
  'Lamu',
  'Machakos',
  'Makueni',
  'Mandera',
  'Marsabit',
  'Meru',
  'Migori',
  'Mombasa',
  "Murang'a",
  'Nairobi',
  'Nakuru',
  'Nandi',
  'Narok',
  'Nyamira',
  'Nyandarua',
  'Nyeri',
  'Samburu',
  'Siaya',
  'Taita-Taveta',
  'Tana River',
  'Tharaka-Nithi',
  'Trans Nzoia',
  'Turkana',
  'Uasin Gishu',
  'Vihiga',
  'Wajir',
  'West Pokot',
] as const;
export type KenyaCounty = (typeof KENYA_COUNTIES)[number];

/** `PropertyImageSerializer` — serializers.ts:76-89. */
export interface PropertyImage {
  id: number;
  property: number | null;
  unit: number | null;
  image: string | null;
  caption: string;
  is_primary: boolean;
  uploaded_at: string;
}

/**
 * `UnitSerializer` — serializers.ts:99-121. Deliberately omits
 * `available_from`, `lease_period`, `is_furnished`, `kitchen_count`,
 * `dining_room_count` — not part of the wire contract (see the backend comment).
 */
export interface Unit {
  id: number;
  property: number;
  unit_number: string;
  bedrooms: number;
  bathrooms: string;
  square_feet: number | null;
  rent_amount: string;
  security_deposit: string;
  is_vacant: boolean;
  last_water_reading: string;
  last_electricity_reading: string;
  description: string;
  images: PropertyImage[];
  tenant_name: string | null;
  active_lease_id: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * `PropertySerializer` — serializers.ts:138-171. Used for retrieve, create,
 * update and the public detail endpoint.
 */
export interface Property {
  id: number;
  owner: number;
  owner_name: string;
  assigned_agent: number | null;
  agent_name: string | null;
  agent_phone: string | null;
  name: string;
  property_type: PropertyType;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  latitude: string | null;
  longitude: string | null;
  description: string;
  year_built: number | null;
  total_units: number;
  status: PropertyStatus;
  water_rate: string;
  electricity_rate: string;
  whatsapp_number: string;
  vacant_units_count: number;
  units: Unit[];
  images: PropertyImage[];
  created_at: string;
  updated_at: string;
  /** Amenity list. Intentional extension beyond DRF — see serializers.ts. */
  features: string[];
}

/**
 * `PropertyListSerializer` — serializers.ts:181-211. Narrower than `Property`:
 * no `owner`/`assigned_agent` ids, and `primary_image` replaces the full
 * `images` array. Used by both `list` (authenticated) and `public/` (list row).
 */
export interface PropertyListRow {
  id: number;
  name: string;
  property_type: PropertyType;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  latitude: string | null;
  longitude: string | null;
  description: string;
  year_built: number | null;
  total_units: number;
  vacant_units_count: number;
  units: Unit[];
  status: PropertyStatus;
  owner_name: string;
  agent_name: string | null;
  agent_phone: string | null;
  whatsapp_number: string;
  primary_image: PropertyImage | null;
  created_at: string;
  /** Amenity list. Intentional extension beyond DRF — see serializers.ts. */
  features: string[];
}

/**
 * `propertyCreateSchema` on write (serializers.ts:225-244). `decimalInput`
 * accepts a string or number on the wire; forms may send either.
 */
export interface PropertyCreateRequest {
  assigned_agent?: number | null | undefined;
  name: string;
  property_type?: PropertyType | undefined;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country?: string | undefined;
  description?: string | undefined;
  year_built?: number | null | undefined;
  total_units?: number | undefined;
  latitude?: number | string | null | undefined;
  longitude?: number | string | null | undefined;
  water_rate?: number | string | undefined;
  electricity_rate?: number | string | undefined;
  whatsapp_number?: string | undefined;
  features?: string[] | undefined;
}

/** `propertyPatchSchema` — every field of `PropertyCreateRequest`, optional. */
export type PropertyPatchRequest = Partial<PropertyCreateRequest>;

/** `unitCreateSchema` on write (serializers.ts:255-267). */
export interface UnitCreateRequest {
  property: number;
  unit_number: string;
  bedrooms?: number | undefined;
  bathrooms?: number | string | undefined;
  square_feet?: number | null | undefined;
  rent_amount: number | string;
  security_deposit?: number | string | undefined;
  is_vacant?: boolean | undefined;
  last_water_reading?: number | string | undefined;
  last_electricity_reading?: number | string | undefined;
  description?: string | undefined;
}

/** `unitPatchSchema` — every field of `UnitCreateRequest`, optional. */
export type UnitPatchRequest = Partial<UnitCreateRequest>;
